import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SiteLayout } from "@/site/SiteLayout";
import { PackageCard, Loading, Empty } from "@/site/parts";
import { ChevronRight, SlidersHorizontal, X } from "lucide-react";

const CATS = [
  { slug: "all", label: "Semua" },
  { slug: "umrah-private", label: "Umrah Private" },
  { slug: "umrah-reguler", label: "Umrah Reguler" },
  { slug: "umrah-mandiri", label: "Umrah Mandiri" },
];

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli",
  "Agustus", "September", "Oktober", "November", "Desember"];

const DURASI = [
  { key: "all", label: "Semua durasi" },
  { key: "short", label: "≤ 9 hari" },
  { key: "mid", label: "10 – 12 hari" },
  { key: "long", label: "≥ 13 hari" },
];

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8A867C]">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-xl border border-[#E5DEC9] bg-white px-3 py-2.5 text-sm text-[#141414] outline-none transition-colors focus:border-[#C5A028]">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

export default function PaketUmrah() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [airline, setAirline] = useState("all");
  const [city, setCity] = useState("all");
  const [month, setMonth] = useState("all");
  const [durasi, setDurasi] = useState("all");
  const [showFilter, setShowFilter] = useState(false);

  const load = useCallback(() => {
    api.get("/public/products", { params: { limit: 100 } })
      .then((r) => setItems(r.data.items.filter((p) => (p.category_slug || "").startsWith("umrah"))))
      .catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const airlines = useMemo(
    () => [...new Set((items || []).map((p) => p.airline).filter(Boolean))].sort(), [items]);
  const cities = useMemo(
    () => [...new Set((items || []).map((p) => p.departure_city).filter(Boolean))].sort(), [items]);

  const filtered = useMemo(() => {
    let rows = items || [];
    if (cat !== "all") rows = rows.filter((p) => p.category_slug === cat);
    if (q) rows = rows.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    if (airline !== "all") rows = rows.filter((p) => p.airline === airline);
    if (city !== "all") rows = rows.filter((p) => p.departure_city === city);
    if (month !== "all") {
      rows = rows.filter((p) => (p.departures || []).some(
        (d) => String(new Date(d.depart_date).getMonth()) === month));
    }
    if (durasi !== "all") {
      rows = rows.filter((p) => {
        const d = p.duration_days || 0;
        if (durasi === "short") return d <= 9;
        if (durasi === "mid") return d >= 10 && d <= 12;
        return d >= 13;
      });
    }
    return rows;
  }, [items, cat, q, airline, city, month, durasi]);

  const activeFilters = [airline, city, month, durasi].filter((v) => v !== "all").length;
  const reset = () => { setAirline("all"); setCity("all"); setMonth("all"); setDurasi("all"); setQ(""); };

  return (
    <SiteLayout>
      <div className="border-b border-[#E5DEC9] bg-white py-12 lg:py-14">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <nav className="flex items-center gap-2 text-xs text-[#8A867C]" data-testid="breadcrumb">
            <Link to="/">Beranda</Link><ChevronRight size={12} /><span>Paket Umrah</span>
          </nav>
          <h1 className="mt-4 font-display text-4xl font-bold text-[#141414] sm:text-5xl">Paket Umrah</h1>
          <p className="mt-3 max-w-2xl text-base text-[#4A4844]">
            Semua pilihan Umrah dalam satu halaman — Private, Reguler, dan Mandiri.
            Saring sesuai maskapai, kota keberangkatan, bulan, dan durasi perjalanan Anda.
          </p>

          <div className="mt-7 flex flex-wrap gap-2" data-testid="category-tabs">
            {CATS.map((c) => (
              <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`tab-cat-${c.slug}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  cat === c.slug ? "bg-[#141414] text-[#E5C358]"
                    : "border border-[#E5DEC9] bg-white text-[#4A4844] hover:border-[#C5A028]"}`}>
                {c.label}
              </button>
            ))}
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama paket..."
              data-testid="paket-search-input"
              className="w-full rounded-xl border border-[#E5DEC9] px-4 py-3 text-sm outline-none focus:border-[#C5A028] sm:max-w-sm" />
            <button onClick={() => setShowFilter(!showFilter)} data-testid="toggle-filter-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-[#141414] px-5 py-3 text-sm font-semibold text-[#141414] transition-colors hover:bg-[#141414] hover:text-[#E5C358]">
              <SlidersHorizontal size={16} /> Filter{activeFilters > 0 ? ` (${activeFilters})` : ""}
            </button>
            {(activeFilters > 0 || q) && (
              <button onClick={reset} data-testid="reset-filter-btn"
                className="flex items-center justify-center gap-1 rounded-xl px-3 py-3 text-sm font-semibold text-[#8A867C] hover:text-[#141414]">
                <X size={14} /> Reset
              </button>
            )}
          </div>

          {showFilter && (
            <div className="mt-5 grid gap-4 rounded-2xl border border-[#E5DEC9] bg-[#FBF9F4] p-5 sm:grid-cols-2 lg:grid-cols-4"
              data-testid="filter-panel">
              <Select label="Maskapai" value={airline} onChange={setAirline} testid="filter-airline"
                options={[{ value: "all", label: "Semua maskapai" },
                  ...airlines.map((a) => ({ value: a, label: a }))]} />
              <Select label="Kota Keberangkatan" value={city} onChange={setCity} testid="filter-city"
                options={[{ value: "all", label: "Semua kota" },
                  ...cities.map((c) => ({ value: c, label: c }))]} />
              <Select label="Bulan Keberangkatan" value={month} onChange={setMonth} testid="filter-month"
                options={[{ value: "all", label: "Semua bulan" },
                  ...BULAN.map((b, i) => ({ value: String(i), label: b }))]} />
              <Select label="Durasi" value={durasi} onChange={setDurasi} testid="filter-duration"
                options={DURASI.map((d) => ({ value: d.key, label: d.label }))} />
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6">
        <p className="mb-6 text-sm text-[#8A867C]" data-testid="paket-count">
          {items === null ? "Memuat paket..." : `${filtered.length} paket ditemukan`}
        </p>
        <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="paket-grid">
          {items === null ? <Loading /> : filtered.length === 0
            ? <Empty label="Tidak ada paket yang cocok dengan filter Anda." />
            : filtered.map((p, i) => <PackageCard key={p.id} p={p} idx={i} />)}
        </div>
      </div>
    </SiteLayout>
  );
}
